import { IMPORT_METHODS } from '@/components/DataIngestion/_constants/data-ingestion';
import { readFromGoogleSheet } from '@/components/DataIngestion/actions/google-sheet';
import { addImportRecord, executeImport, ExecuteImportResult } from '@/components/DataIngestion/actions/import/import';
import { googleSheetToImportData } from '@/components/DataIngestion/utils/google-sheet';
import { Button, LoadingOverlay, toast } from '@payloadcms/ui';
import { useState } from 'react';

import ConnectedToGoogleSheets from '../../../../../components/ConnectedToGoogleSheets';
import GoogleSheetsConnectionManager from '../../../../../components/GoogleSheetsConnectionManager';
import { FromExportImportComponentProps } from '../../FromExport';

import './styles.scss';

import { IMPORT_JOURNEY_MODAL_SLUG } from '../../../../ImportModal';
import ImportResult from '../../../ImportResult/ImportResult';

const GoogleSheetImportFromExport = ({ exportDocument }: FromExportImportComponentProps) => {
  const googleSheetId = exportDocument.export?.method?.[0]?.sheetId;
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<ExecuteImportResult | null>(null);

  if (!googleSheetId) {
    throw new Error('Google Sheet ID is required');
  }

  const googleSheetUrl = `https://docs.google.com/spreadsheets/d/${googleSheetId}`;

  const handleImport = async () => {
    try {
      setIsLoading(true);
      const { data } = await readFromGoogleSheet(googleSheetId);

      if (!data) {
        throw new Error('Failed to read data from Google Sheet');
      }

      const importData = googleSheetToImportData(data);
      const result = await executeImport({
        importData,
        collectionSlug: exportDocument.targetCollectionSlug,
      });

      setImportResult(result);

      if (result.documentsUpdated > 0) {
        await addImportRecord({
          method: {
            blockType: IMPORT_METHODS.FROM_EXPORT,
          },
          exportId: exportDocument.id,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to import data from Google Sheet');
    } finally {
      setIsLoading(false);
    }
  };

  if (importResult) {
    return <ImportResult importResult={importResult} modalSlug={IMPORT_JOURNEY_MODAL_SLUG} />;
  }

  if (isLoading) {
    return <LoadingOverlay loadingText="Importing data from Google Sheet..." />;
  }

  return (
    <GoogleSheetsConnectionManager>
      {({ status, connect }) => (
        <div className="fv-google-sheet-import-from-export">
          <h2>Import data from Google Sheet</h2>
          <ConnectedToGoogleSheets status={status} />
          <a href={googleSheetUrl} target="_blank" rel="noopener noreferrer">
            {googleSheetUrl}
          </a>
          <div className="fv-google-sheet-import-from-export__actions">
            <Button onClick={handleImport} buttonStyle="primary">
              Import
            </Button>
            <Button onClick={connect} buttonStyle="secondary">
              Sign in with a different account
            </Button>
          </div>
        </div>
      )}
    </GoogleSheetsConnectionManager>
  );
};

export default GoogleSheetImportFromExport;
